/** tm/TaskManagement.tsx — 메인 레이아웃 + 사이드바 (navParams 지원) */
import { useState, useCallback, useEffect, useRef } from 'react';
import { AccountBadge } from '../AccountSwitch';
import { C, T } from './components/ui';
import { useTMStore } from './hooks';
import DashboardPage from './pages/DashboardFlowPage';   // 2026-10-12 업무진행 4차 시안 — 옛 화면은 pages/DashboardPage.tsx(되돌리기용)
import ConsultationPage from './pages/ConsultationPage';
import CompanyListPage from './pages/CompanyListPage';
import TaskByTypePage from './pages/TaskByTypePage';
import FPManagePage from './pages/FPManagePage';
import WMManagePage from './pages/WMManagePage';
import GovPage from './pages/GovPage';
import NewsletterPage from './pages/NewsletterPage';
import FieldNotesPage from './pages/FieldNotesPage';
import SettingsPage from './pages/SettingsPage';
import TodosPage from './pages/TodosPage';
import LecturePage from './pages/LecturePage';
import LoanBoardPage from './pages/LoanBoardPage';
import FundAdvisorPage from './pages/FundAdvisorPage';
import ClientDeskPage from './pages/ClientDeskPage';
import InputCheckPage from './pages/InputCheckPage';
import * as api from './api';
import LegalHubPage from './legal/LegalHubPage';
import { useLegalAlerts } from './legal/useLegalAlerts';
import LegalNewsPopup from './legal/LegalNewsPopup';
import type { LegalNavParams } from './legal/legalTypes';
import { useWindowWidth } from '../shared';
type Section = 'dashboard'|'inputs'|'loans'|'advisor'|'clientdesk'|'consultation'|'lectures'|'companies'|'tasktype'|'fps'|'taxes'|'wms'|'gov'|'newsletter'|'legal'|'notes'|'todos'|'settings';
interface MenuItem { id: Section; icon: string; label: string }
const MENU: MenuItem[] = [
  { id:'dashboard',icon:'📈',label:'대시보드' },
  { id:'inputs',icon:'🧾',label:'빠진 입력' },   // 2026-10-18 — 업무 입력이 빠져 고객 · 팀장님 화면이 틀리게 보이는 줄을 한 목록에서 그 자리 고치기(오재인)
  { id:'todos',icon:'📌',label:'일일 업무' },
  { id:'loans',icon:'💰',label:'대출 점검' },
  { id:'advisor',icon:'🧭',label:'정책자금 추천' },   // 2026-10-30 — 고객과 통화하며 클릭 · 중진공 · 소진공 상품 추천(틀)
  { id:'clientdesk',icon:'⏰',label:'거래처 기한' },   // 2026-10-24 — fphub 거래처 관리(팀장님 기한 · 처리 요청 · 이어진 팀장님 · 규칙표 · 아침 문자)   // 2026-10-13 — 진행 중인 대출을 매일 확인 · 소식
  { id:'consultation',icon:'🤝',label:'동행상담' },
  { id:'lectures',icon:'🎤',label:'강의 스케줄' },
  { id:'companies',icon:'🏢',label:'업체별 관리' },
  { id:'tasktype',icon:'📊',label:'업무별 관리' },
  { id:'fps',icon:'👥',label:'FP별 관리' },
  { id:'taxes',icon:'🧾',label:'세무사별 관리' },
  { id:'wms',icon:'🏆',label:'WM별 관리' },
  { id:'gov',icon:'🏛️',label:'정부지원사업' },
  { id:'newsletter',icon:'📰',label:'월간 소식지' },   // 달마다 소식지 «담을 범위» (2026-10-09)
  { id:'legal',icon:'📚',label:'법령·판례·뉴스' },
  { id:'notes',icon:'💡',label:'현장 노트' },   // 2026-11-01 — 상담에서 배운 점 · 현장에서 배운 서비스 아이디어(열람 · 검색 · 반영 상태)
  { id:'settings',icon:'⚙️',label:'설정' },
];
interface Props {
  me: { id?: string; name: string; branding?: { name?: string }; brandingName?: string; senderName?: string };
  onBack: () => void;
}
export default function TaskManagement({ me, onBack }: Props) {
  // HomeScreen에서 'go-to-todos' 신호 받은 경우 시작 섹션 = todos
  const [section, setSection] = useState<Section>(() => {
    try {
      const init = localStorage.getItem('tm_initial_section');
      if (init === 'todos') { localStorage.removeItem('tm_initial_section'); return 'todos'; }
      if (sessionStorage.getItem('tm_gov_prog')) return 'gov';   // fphub 리포트에서 공고 상세로 (App.jsx 「gov_prog」)
    } catch { /* ignore */ }
    return 'dashboard';
  });
  const [navParams, setNavParams] = useState<Record<string,unknown>>({});
  const store = useTMStore();
  const brandName = me.brandingName || me.branding?.name || '로움파트너스';
  // 정부지원사업 SMS 발신 서명 — 계정 설정값을 쓰고, 없으면 브랜드/이름으로 대체 (NotifyScreen.jsx 와 같은 순서)
  const smsSenderName = me.senderName || me.branding?.name || me.brandingName || me.name || '관리자';
  const menuItem = MENU.find(m=>m.id===section);
  const myId = me.id || '';
  const legalAlerts = useLegalAlerts(section); // 법령·판례·뉴스 새 소식 (보이는 동안 60초·숨김 5분 폴링 + 포커스·메뉴 이동 시, 탭 제목 "(n) ")
  // 모바일(<860px): 사이드바를 ☰ 버튼으로 여는 서랍으로 (데스크톱은 그대로)
  const narrow = useWindowWidth() < 860;
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { if (!narrow) setNavOpen(false); }, [narrow]);
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);
  const [todoToast, setTodoToast] = useState<{msg:string;kind?:'success'|'error'}|null>(null);
  // 빠진 입력 — 메뉴 빨간 숫자(꼭 고칠 것) · 처음 · 메뉴 이동 때 · 5분마다 (2026-10-18)
  const [inputMust, setInputMust] = useState(0);
  useEffect(() => {
    let off = false;
    // 실무 담당 계정은 자기 담당 + 공통(상담) 줄만 센다
    const staff = ['오재인', '임우승'].includes(me.name);
    const get = () => { api.getInputCheck().then(r => { if (!off && r.success && r.data) setInputMust(staff ? r.data.items.filter(x => x.level === 'must' && (!x.who || x.who === me.name)).length : r.data.counts.must); }).catch(() => { /* 숫자 없이 */ }); };
    get();
    const h = window.setInterval(get, 5 * 60 * 1000);
    return () => { off = true; window.clearInterval(h); };
  }, [section, me.name]);

  const navTo = useCallback((id: string, params?: unknown) => {
    setSection(id as Section);
    setNavParams(params ? { [id]: params } : {});
  }, []);

  // 브라우저 뒤로가기 (App.jsx에서 tm-back 이벤트 발생)
  useEffect(() => {
    const handleBack = () => {
      setSection(prev => {
        if (prev === 'dashboard') {
          onBack();
          return prev;
        }
        return 'dashboard';
      });
    };
    window.addEventListener('tm-back', handleBack);
    return () => window.removeEventListener('tm-back', handleBack);
  }, [onBack]);

  // 화면 틀(전체·콘텐츠 칸)은 스크롤되면 안 된다 — 포커스·scrollIntoView 등이 overflow:hidden 칸이나 문서 자체를 밀어 올리면
  // 위가 잘리고 아래가 빈 칸으로 보인다(2026-10-01 공고 검토함). 밀리는 즉시 제자리로.
  const rootRef=useRef<HTMLDivElement>(null);
  const colRef=useRef<HTMLDivElement>(null);
  const bodyRef=useRef<HTMLDivElement>(null);
  useEffect(()=>{
    const els=[rootRef.current,colRef.current,bodyRef.current].filter((e):e is HTMLDivElement=>!!e);
    const reset=(el:HTMLElement)=>()=>{if(el.scrollTop||el.scrollLeft){el.scrollTop=0;el.scrollLeft=0;}};
    const offs=els.map(el=>{const h=reset(el);el.addEventListener('scroll',h);h();return()=>el.removeEventListener('scroll',h);});
    const win=()=>{if(window.scrollY||window.scrollX)window.scrollTo(0,0);};
    window.addEventListener('scroll',win);win();
    return()=>{offs.forEach(f=>f());window.removeEventListener('scroll',win);};
  },[store.loading,store.error]);

  if(store.loading)return(<div style={{minHeight:'100vh',background:C.bg,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{textAlign:'center'}}><div style={{fontSize:40,marginBottom:12}}>⏳</div><div style={{color:C.muted,fontSize:T.md}}>업무관리 데이터를 불러오는 중...</div></div></div>);
  if(store.error)return(<div style={{minHeight:'100vh',background:C.bg,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{textAlign:'center',maxWidth:400}}><div style={{fontSize:40,marginBottom:12}}>⚠️</div><div style={{color:'#DC2626',fontSize:T.md,marginBottom:12}}>{store.error}</div><button onClick={store.refresh} style={{padding:'10px 24px',borderRadius:8,border:'none',background:C.accent,color:C.white,fontSize:T.md,cursor:'pointer'}}>다시 시도</button></div></div>);

  return(
    <div ref={rootRef} style={{display:'flex',height:'100vh',overflow:'hidden',background:C.bg}}>
      {/* 사이드바 */}
      <div onClick={narrow?()=>setNavOpen(false):undefined} aria-hidden={narrow&&!navOpen?true:undefined} style={narrow?{position:'fixed',top:0,left:0,bottom:0,zIndex:1500,display:'flex',transform:navOpen?'translateX(0)':'translateX(-100%)',visibility:navOpen?'visible':'hidden',transition:'transform .2s ease, visibility .2s',boxShadow:navOpen?'4px 0 24px rgba(0,0,0,0.3)':'none'}:{display:'contents'}}>
      <div style={{width:180,background:C.primary,display:'flex',flexDirection:'column',flexShrink:0}}>
        <div style={{padding:'14px 14px 10px',borderBottom:'1px solid rgba(255,255,255,0.1)'}}>
          <div style={{fontSize:T.md,fontWeight:800,color:'#fff'}}>📋 업무관리</div>
          <div style={{fontSize:T.xs,color:'rgba(255,255,255,0.5)',marginTop:2}}>{brandName}</div>
        </div>
        <div style={{flex:1,padding:'6px 0',overflowY:'auto'}}>
          {MENU.map(m=>{const act=section===m.id;return(
            <div key={m.id} onClick={()=>navTo(m.id)} style={{padding:'9px 14px',display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:T.sm,background:act?C.sideHover:'transparent',color:act?'#fff':'rgba(255,255,255,0.6)',fontWeight:act?700:400}}>
              <span>{m.icon}</span><span>{m.label}</span>
              {m.id==='inputs'&&inputMust>0&&<span aria-label={`꼭 고칠 것 ${inputMust}건`} style={{marginLeft:'auto',background:C.red,color:'#fff',borderRadius:9,fontSize:11,fontWeight:700,padding:'1px 6px',fontVariantNumeric:'tabular-nums'}}>{inputMust}</span>}
              {m.id==='legal'&&legalAlerts.total>0&&<span key={legalAlerts.pulse} className="lw-pulse" aria-label={`새 소식 ${legalAlerts.total}건`} style={{marginLeft:'auto',background:C.red,color:'#fff',borderRadius:9,fontSize:11,fontWeight:700,padding:'1px 6px',fontVariantNumeric:'tabular-nums'}}>{legalAlerts.total}</span>}
            </div>
          );})}
        </div>
        <div style={{padding:'8px 10px',borderTop:'1px solid rgba(255,255,255,0.1)'}}>
          <button onClick={()=>{if(section!=='dashboard'){navTo('dashboard');}else{onBack();}}} style={{width:'100%',padding:'7px 8px',borderRadius:6,border:'1px solid rgba(255,255,255,0.15)',background:'transparent',color:'rgba(255,255,255,0.6)',fontSize:T.xs,cursor:'pointer'}}>{section==='dashboard'?'← 홈으로':'← 대시보드'}</button>
        </div>
      </div>
      </div>
      {narrow&&navOpen&&<div onClick={()=>setNavOpen(false)} aria-hidden="true" style={{position:'fixed',inset:0,background:'rgba(17,24,39,0.45)',zIndex:1400}}/>}
      {/* 콘텐츠 */}
      <div ref={colRef} style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{background:C.white,borderBottom:`1px solid ${C.border}`,padding:'0 20px',display:'flex',alignItems:'center',justifyContent:'space-between',height:46,flexShrink:0}}>
          {narrow&&<button type="button" onClick={()=>setNavOpen(true)} aria-label="메뉴 열기" aria-expanded={navOpen} style={{marginLeft:-8,marginRight:6,width:40,height:40,border:'none',background:'transparent',fontSize:20,color:C.text,cursor:'pointer',borderRadius:8,flexShrink:0}}>☰</button>}
          <span style={{fontSize:T.lg,fontWeight:700,color:C.text}}>{menuItem?.icon} {menuItem?.label||'업무관리'}</span>
          {narrow&&<span style={{flex:1}}/>}
          <AccountBadge name={me.name} style={{background:C.accentLt,color:C.accent,fontSize:T.xs}}/>
        </div>
        <div ref={bodyRef} style={{flex:1,overflow:'hidden'}}>
          {section==='dashboard'&&<DashboardPage store={store} onNavigate={navTo} me={me.name}/>}
          {section==='inputs'&&<InputCheckPage store={store} onNavigate={navTo} onCount={setInputMust} me={me.name}/>}
          {section==='loans'&&<LoanBoardPage onToast={(msg,kind)=>setTodoToast({msg,kind:kind==='error'?'error':'success'})}/>}
          {section==='advisor'&&<FundAdvisorPage onToast={(msg,kind)=>setTodoToast({msg,kind:kind==='error'?'error':'success'})}/>}
          {section==='clientdesk'&&<ClientDeskPage onOpenCompany={(companyId)=>navTo('companies',{companyId})} onToast={(msg,kind)=>setTodoToast({msg,kind:kind==='error'?'error':'success'})}/>}
          {section==='consultation'&&<ConsultationPage store={store} onNavigate={navTo}/>}
          {section==='lectures'&&<LecturePage store={store} navParams={navParams['lectures'] as {lectureId?:string}|undefined} onNavigate={navTo} onToast={(msg,kind)=>setTodoToast({msg,kind})}/>}
          {section==='companies'&&<CompanyListPage store={store} navParams={navParams['companies'] as {companyId?:string;taskId?:string;watch?:{id:string;name:string}}|undefined}/>}
          {section==='tasktype'&&<TaskByTypePage store={store} navParams={navParams['tasktype'] as {tplId?:string;highlightKey?:string}|undefined} onNavigate={navTo}/>}
          {section==='fps'&&<FPManagePage key="fp" store={store}/>}
          {section==='taxes'&&<FPManagePage key="tax" store={store} role="tax"/>}
          {section==='wms'&&<WMManagePage store={store}/>}
          {section==='gov'&&<GovPage store={store} senderName={smsSenderName}/>}
          {section==='newsletter'&&<NewsletterPage/>}
          {section==='legal'&&<LegalHubPage store={store} isSuper={!!(me as { isSuper?: boolean }).isSuper} alerts={legalAlerts} userId={myId} navParams={navParams['legal'] as LegalNavParams|undefined}/>}
          {section==='todos'&&<TodosPage myId={myId} onToast={(msg,kind)=>setTodoToast({msg,kind})}/>}
          {section==='notes'&&<FieldNotesPage onToast={(msg,kind)=>setTodoToast({msg,kind})}/>}
          {section==='settings'&&<SettingsPage store={store} navParams={navParams['settings'] as {tab?:string}|undefined} onNavigate={navTo}/>}
        </div>
      </div>

      {/* 법령·판례·뉴스 「새 소식」 — 어느 화면에서든 새 알림이 오면 오른쪽 아래(모바일은 아래쪽)에 카드로 */}
      <LegalNewsPopup alerts={legalAlerts} userId={myId} mobile={narrow} onOpen={p=>{setNavOpen(false);navTo('legal',p);}}/>
      {/* Toast */}
      {todoToast && (
        <div onClick={()=>setTodoToast(null)} style={{position:'fixed',bottom:20,left:'50%',transform:'translateX(-50%)',background:todoToast.kind==='error'?'#DC2626':'#16A34A',color:'#fff',padding:'10px 18px',borderRadius:8,fontSize:T.sm,fontWeight:600,boxShadow:'0 4px 12px rgba(0,0,0,0.2)',cursor:'pointer',zIndex:2000}}>{todoToast.msg}</div>
      )}
    </div>
  );
}
